import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { Divider, Spin, Button, notification, Modal } from 'antd';
import { LeftOutlined, RightOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import './FileNavigator.less';
import ListView from '../ListView';
import LocationBar from '../LocationBar';
import Notifications from '../Notifications';
import Toolbar from '../Toolbar';
import { SortDirection } from 'react-virtualized';
import { find, isEqual } from 'lodash';
import clickOutside from 'react-click-outside';

import rawToReactElement from '../raw-to-react-element';
import { createHistory, pushToHistory } from '../history';

import { ItemTypes } from '../../../../constants/constants';

import { unsupportedFileTypeDefaultMsg, isValidFileBeforeUpload } from '../../../../../../shared/utils/files/file-upload-accepts';
import { normalizeTransferItem } from '../../../../../../shared/utils/files/common';

function hasContext(capability, context) {
  return capability.availableInContexts && capability.availableInContexts.indexOf(context) !== -1;
}

const propTypes = {
  id: PropTypes.string,
  api: PropTypes.object,
  apiOptions: PropTypes.object,
  capabilities: PropTypes.func,
  className: PropTypes.string,
  initialResourceId: PropTypes.string,
  listViewLayout: PropTypes.func,
  viewLayoutOptions: PropTypes.object,
  signInRenderer: PropTypes.func,
  onClickOutside: PropTypes.func,
  onResourceItemClick: PropTypes.func,
  onResourceItemRightClick: PropTypes.func,
  onResourceItemDoubleClick: PropTypes.func,
  onResourceLocationChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onResourceChange: PropTypes.func,
  onResourceChildrenChange: PropTypes.func,
  handleOpenFilePreview: PropTypes.func,
  isOnCertsPage: PropTypes.bool,
};

const defaultProps = {
  id: '',
  api: 'nodeV1',
  apiOptions: {
    locale: 'en',
  },
  capabilities: () => [],
  className: '',
  initialResourceId: '',
  listViewLayout: () => {},
  viewLayoutOptions: {},
  signInRenderer: null,
  onClickOutside: ({ fileNavigator }) => fileNavigator.handleSelectionChange([]),
  onResourceItemClick: () => {},
  onResourceItemRightClick: () => {},
  onResourceItemDoubleClick: () => {},
  onResourceLocationChange: () => {},
  onSelectionChange: () => {},
  onResourceChange: () => {},
  onResourceChildrenChange: () => {},
  isOnCertsPage: false,
};

const MONITOR_API_AVAILABILITY_TIMEOUT = 16;

@clickOutside
export default class FileNavigator extends Component {
  state = {
    apiInitialized: false,
    apiSignedIn: false,
    config: {},
    dialogElement: null,
    error: null,
    loadingResourceLocation: false,
    loadingView: false,
    notifications: [],
    resource: {},
    resourceChildren: [],
    resourceLocation: [],
    history: createHistory(),
    selection: [],
    sortBy: 'title',
    sortDirection: SortDirection.ASC,
    initializedCapabilities: [],
    searchString: null,
    locationBarVisible: true,
    totalColumnWidth: 1150,
    columnWidths: {
      name: 340,
      description: 140,
      size: 100,
      uploader: 100,
      createDate: 130,
      changeDate: 130,
    },
  };

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;

    if (window.ncFileManagerSettings && window.ncFileManagerSettings.popupMode) {
      this.setState({
        resource: {},
      });
    }

    this.initialize();
  }

  componentWillReceiveProps(nextProps) {
    const needToNavigate =
      this.props.initialResourceId !== nextProps.initialResourceId && (this.state.resource && this.state.resource.id) !== nextProps.initialResourceId;

    if (needToNavigate) {
      this.navigateToDir(nextProps.initialResourceId);
    }

    if (!isEqual(this.props.apiOptions, nextProps.apiOptions)) {
      const { apiOptions, capabilities } = nextProps;
      const capabilitiesProps = this.getCapabilitiesProps();
      const initializedCapabilities = capabilities(apiOptions, capabilitiesProps);
      this.setState({ initializedCapabilities });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setStateAsync = (...args) => {
    if (this._isMounted) {
      this.setState(...args);
    }
  };

  initialize = async () => {
    const { apiOptions, api, capabilities, viewLayoutOptions, controlConfiguration, isOnCertsPage } = this.props;

    const capabilitiesProps = this.getCapabilitiesProps();
    const initializedCapabilities = capabilities(apiOptions, capabilitiesProps);

    const { apiInitialized, apiSignedIn } = await api.init({
      ...apiOptions,
    });

    const sortBy = (controlConfiguration ? controlConfiguration.sortBy : '') || viewLayoutOptions.initialSortBy || 'title';
    const sortByDirection = (controlConfiguration ? controlConfiguration.sortDirection : '') || viewLayoutOptions.initialSortDirection || 'ASC';

    this.setStateAsync(
      {
        apiInitialized,
        apiSignedIn,
        initializedCapabilities,
        sortBy: sortBy,
        sortDirection: sortByDirection,
        locationBarVisible: !isOnCertsPage,
      },
      (_) => {
        if (apiSignedIn) {
          this.handleApiReady();
        } else {
          if (apiInitialized) {
            this.handleApiSignInFail();
          } else {
            this.handleApiInitFail();
          }

          this.monitorApiAvailability();
        }
      },
    );
  };

  startViewLoading = () => {
    this.setStateAsync({ loadingView: true });
  };

  stopViewLoading = () => {
    this.setStateAsync({ loadingView: false });
  };

  handleApiReady = () => {
    const { initialResourceId } = this.props;
    const resourceId = this.state.resource.id;
    const idToNavigate = typeof resourceId === 'undefined' ? initialResourceId : resourceId;
    this.navigateToDir(idToNavigate);
    this.setParentsForResource(this.state.resource.id);
  };

  monitorApiAvailability = () => {
    const { api } = this.props;

    this.apiAvailabilityTimeout = setTimeout(() => {
      if (api.hasSignedIn()) {
        this.setStateAsync({ apiInitialized: true, apiSignedIn: true });
        this.handleApiReady();
      } else {
        this.monitorApiAvailability();
      }
    }, MONITOR_API_AVAILABILITY_TIMEOUT);
  };

  handleApiInitFail = () => {
    this.handleResourceChildrenChange([]);
  };

  handleApiSignInFail = () => {
    this.handleSelectionChange([]);
    this.handleResourceChildrenChange([]);
    this.handleResourceChange({});
  };

  handleLocationBarChange = (id) => {
    const { resource } = this.state;
    this.navigateToDir(id, resource.id);
  };

  handleHistoryChange = (history) => {
    this.setStateAsync({ history });

    const navigateToId = history.stack[history.currentPointer];
    this.navigateToDir(navigateToId, null, true, false);
  };

  handleRefresh = () => {
    const { resource } = this.state;
    this.navigateToDir(resource.id, null, true, false);
    this.setParentsForResource(resource.id);
  };

  handleCopyDefaultFolderStructure = async () => {
    const { api } = this.props;

    const result = await api.copyDefaultFolderStructure();

    if (result) {
      this.handleRefresh();
    }
  };

  navigateToDir = async (toId, idToSelect, startLoading = true, changeHistory = true) => {
    const { history, sortBy, sortDirection } = this.state;

    if (startLoading) {
      this.startViewLoading();
    }

    let resource = null;
    let resourceChildren = null;
    if (toId.toString().endsWith('_onedrive')) {
      const { api } = this.props;

      const collbackAndNavigateToDir = async () => {
        await this.oneDriveLoginCallback(async () => await this.navigateToDir(toId, idToSelect, startLoading, changeHistory));
      };

      const collbackGetExternalIdAndNavigateToDir = async () => {
        const id = await api.getRootFolderExternalId();
        await this.navigateToDir(id, idToSelect, startLoading, changeHistory);
      };

      if (toId == '00000000-0000-0000-0000-000000000000_onedrive') {
        this.openSelectOneDriveRootFolderDialog(collbackGetExternalIdAndNavigateToDir, collbackAndNavigateToDir);
        return;
      } else {
        resource = await api.getOneDriveFolderById(toId, collbackAndNavigateToDir);
        resourceChildren = await api.getOneDriveFolderChildren({ id: resource.id }, collbackAndNavigateToDir);
      }
    } else if (toId.toString().endsWith('_googledrive')) {
      const { api } = this.props;

      const collbackAndNavigateToDirGoogleDrive = async () => {
        await this.googleDriveLoginCallback(async () => {
          await this.navigateToDir(toId, idToSelect, startLoading, changeHistory);
        });
      };

      const collbackGetExternalIdAndNavigateToDirGoogleDrive = async () => {
        const id = await api.getGoogleDriveRootFolderExternalId();
        await this.navigateToDir(id, idToSelect, startLoading, changeHistory);
      };

      if (toId == '00000000-0000-0000-0000-000000000000_googledrive') {
        this.openSelectGoogleDriveRootFolderDialog(collbackGetExternalIdAndNavigateToDirGoogleDrive, collbackAndNavigateToDirGoogleDrive);
        return;
      } else {
        resource = await api.getGoogleDriveFolderById(toId, collbackAndNavigateToDirGoogleDrive);
        resourceChildren = await api.getGoogleDriveFolderChildren({ id: resource.id }, collbackAndNavigateToDirGoogleDrive);
      }
    } else {
      resource = await this.getResourceById(toId);
      resourceChildren = await this.getChildrenForId(resource.id, resource.systemFolderType, sortBy, sortDirection);
    }

    this.handleResourceChange(resource);

    if (this.props.filterResourceChildren) {
      resourceChildren = resourceChildren.filter(this.props.filterResourceChildren);
    }

    if (changeHistory) {
      this.setStateAsync({ history: pushToHistory(history, toId) });
    }

    Promise.all([resource, resourceChildren]).then(() => {
      this.handleSelectionChange([resource.id, idToSelect]);
    });

    this.handleResourceChildrenChange(resourceChildren);

    this.stopViewLoading();
  };

  oneDriveLoginCallback = async (afterAction) => {
    const oneDriveLoginUrl = await this.props.api.getOneDriveCodeRequestUrl();

    const oneDriveLoginWindow = window.open(oneDriveLoginUrl, '_blank', 'width=600,height=400');

    if (oneDriveLoginWindow == null) {
      message.error({
        content: 'Your browser blocked the window.',
        className: 'custom-class',
        style: {
          marginTop: '20vh',
        },
      });

      return;
    }

    oneDriveLoginWindow.focus();

    localStorage.removeItem('onedrive_login_result');

    const loginResultListener = (ev) => {
      if (ev.key == 'onedrive_login_result') {
        const message = JSON.parse(ev.newValue);
        if (message && message.isSuccess) {
          if (typeof afterAction === 'function') {
            afterAction();
          }
        }
      }

      window.removeEventListener('storage', loginResultListener);
    };

    window.addEventListener('storage', loginResultListener);

    const timer = setInterval(() => {
      if (oneDriveLoginWindow.closed) {
        clearInterval(timer);
        this.stopViewLoading();
      }
    }, 1000);
  };

  googleDriveLoginCallback = async (afterAction) => {
    const googleDriveLoginUrl = await this.props.api.getGoogleDriveCodeRequestUrl();

    const googleDriveLoginWindow = window.open(googleDriveLoginUrl, '_blank', 'width=600,height=400');

    if (googleDriveLoginWindow == null) {
      message.error({
        content: 'Your browser blocked the window.',
        className: 'custom-class',
        style: {
          marginTop: '20vh',
        },
      });

      return;
    }

    googleDriveLoginWindow.focus();

    localStorage.removeItem('googledrive_login_result');

    const loginResultListener = (ev) => {
      if (ev.key == 'googledrive_login_result') {
        const message = JSON.parse(ev.newValue);
        if (message && message.isSuccess) {
          if (typeof afterAction === 'function') {
            afterAction();
          }
        }
      }

      window.removeEventListener('storage', loginResultListener);
    };

    window.addEventListener('storage', loginResultListener);

    const timer = setInterval(() => {
      if (googleDriveLoginWindow.closed) {
        clearInterval(timer);
        this.stopViewLoading();
      }
    }, 1000);
  };

  openSelectOneDriveRootFolderDialog = (collbackGetExternalIdAndNavigateToDir, collbackAndNavigateToDir) => {
    const { api, rootContainer } = this.props;

    api.getOneDriveFolderChildren({ excludeFiles: true }, collbackAndNavigateToDir).then((oneDriveFolders) => {
      const rawDialogElement = {
        elementType: 'SelectOneDriveRootFolderDialog',
        elementProps: {
          onHide: this.hideDialog,
          rootContainer: rootContainer,
          children: oneDriveFolders,
          getChildren: api.getOneDriveFolderChildren,
          assignRootFolderExternalId: api.assignRootFolderExternalId,
          collbackGetExternalIdAndNavigateToDir: collbackGetExternalIdAndNavigateToDir,
          refresh: this.handleRefresh,
        },
      };

      this.showDialog(rawDialogElement);
    });
  };

  openSelectGoogleDriveRootFolderDialog = (collbackGetExternalIdAndNavigateToDir, collbackAndNavigateToDir) => {
    const { api, rootContainer } = this.props;

    api.getGoogleDriveFolderChildren({ excludeFiles: true }, collbackAndNavigateToDir).then((googleDriveFolders) => {
      const rawDialogElement = {
        elementType: 'SelectGoogleDriveRootFolderDialog',
        elementProps: {
          onHide: this.hideDialog,
          rootContainer: rootContainer,
          children: googleDriveFolders,
          getChildren: api.getGoogleDriveFolderChildren,
          assignRootFolderExternalId: api.assignGoogleDriveRootFolderExternalId,
          collbackGetExternalIdAndNavigateToDir: collbackGetExternalIdAndNavigateToDir,
          refresh: this.handleRefresh,
        },
      };

      this.showDialog(rawDialogElement);
    });
  };

  async setParentsForResource(resourceId) {
    this.setStateAsync({ loadingResourceLocation: true });
    const resourceParents = await this.getParentsForId(resourceId);
    this.handleResourceLocationChange(resourceParents);
    this.setStateAsync({ loadingResourceLocation: false });
  }

  async getParentsForId(id) {
    const { api, apiOptions } = this.props;
    return await api.getParentsForId(apiOptions, id);
  }

  async getFolderChildrenForId(id) {
    const { api, apiOptions } = this.props;
    return await api.getFolderChildrenForId(id);
  }

  async getResourceById(id) {
    const { api, apiOptions } = this.props;
    const result = await api.getResourceById(apiOptions, id);
    return result;
  }

  async getChildrenForId(id, systemFolderType, sortBy, sortDirection) {
    const { api, apiOptions } = this.props;
    return api.getChildrenForId(apiOptions, { id, systemFolderType, sortBy, sortDirection });
  }

  getResourceChildrenBySelection(selection) {
    const { resourceChildren } = this.state;
    const filteredResourceItems = resourceChildren.filter((o) => selection.indexOf(o.id) !== -1);
    return filteredResourceItems;
  }

  handleClickOutside = () => {
    if (this.props.onClickOutside) {
      this.props.onClickOutside({ fileNavigator: this });
    }
  };

  handleResourceLocationChange = (resourceLocation) => {
    this.setStateAsync({ resourceLocation });
    this.props.onResourceLocationChange(resourceLocation);
  };

  handleSelectionChange = (selection) => {
    this.setStateAsync({ selection });
    this.props.onSelectionChange(selection);
  };

  handleResourceChildrenChange = (resourceChildren, searchString = null) => {
    this.setStateAsync({ resourceChildren, searchString });
    this.props.onResourceChildrenChange(resourceChildren);
  };

  handleResourceChange = (resource) => {
    this.setStateAsync({ resource });
    this.props.onResourceChange(resource);
  };

  handleSort = async ({ sortBy, sortDirection }) => {
    const { initializedCapabilities } = this.state;
    const sortCapability = find(initializedCapabilities, (o) => o.id === 'sort');
    if (!sortCapability) {
      return;
    }

    if (this.state.searchString) {
      this.setStateAsync({ sortBy, sortDirection });

      this.search(this.state.searchString);

      return;
    }

    const sort = sortCapability.handler;
    this.setStateAsync({ loadingView: true });
    const newResourceChildren = await sort({ sortBy, sortDirection });
    this.handleResourceChildrenChange(newResourceChildren);
    this.setStateAsync({ sortBy, sortDirection, loadingView: false });

    if (this.props.controlConfiguration && typeof this.props.api.saveFilesControlConfiguration === 'function') {
      const controlConfig = {
        sortBy,
        sortDirection,
      };

      await this.props.api.saveFilesControlConfiguration(controlConfig, this.props.isSendFiles);
    }
  };

  handleMoveItems = async (sourceItemIds, targetItemId) => {
    const { api, apiOptions, rootContainer } = this.props;
    const resource = await this.getResourceById(sourceItemIds[0]);
    if (resource.parentId !== targetItemId) {
      const selectedResources = this.state.resourceChildren.filter((obj) => sourceItemIds.includes(obj.id)) || [];
      api
        .getFilesInDestinationFolder(
          apiOptions,
          selectedResources.map((x) => x.name),
          targetItemId,
        )
        .then(async (existingFiles) => {
          if (existingFiles.length) {
            const existingfileNames = _.uniqBy(existingFiles, 'name').map((x) => x.name);

            showRenameSelectedFilesModal(
              existingfileNames,
              rootContainer,
              () => {
                api
                  .moveResources(
                    apiOptions,
                    selectedResources.map((x) => normalizeTransferItem(x)),
                    targetItemId,
                  )
                  .then((res) => {
                    this.navigateToDir(targetItemId);
                    this.setParentsForResource(targetItemId);
                  });
              },
              () => {
                const choosenItems = selectedResources.filter((f) => !existingfileNames.includes(f.name)) || [];
                const transferItems = choosenItems.length ? choosenItems.map((x) => normalizeTransferItem(x)) : null;

                if (transferItems && transferItems.length) {
                  api.moveResources(apiOptions, transferItems, targetItemId).then((res) => {
                    this.navigateToDir(targetItemId);
                    this.setParentsForResource(targetItemId);
                  });
                }
              },
            );
          } else {
            api
              .moveResources(
                apiOptions,
                selectedResources.map((x) => normalizeTransferItem(x)),
                targetItemId,
              )
              .then((res) => {
                this.navigateToDir(targetItemId);
                this.setParentsForResource(targetItemId);
              });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  handleUploadDropedFiles = async (targetItemId, files, navigateDirId, filesToUploadCount) => {
    try {
      const { api, apiOptions } = this.props;
      const { initializedCapabilities } = this.state;

      const uploadCapability = find(initializedCapabilities, (o) => o.id === 'upload');
      const capabilityProps = this.getCapabilitiesProps();
      capabilityProps.getResource = () => {
        return { id: targetItemId, navigateDirId: navigateDirId, filesToUploadCount: filesToUploadCount };
      };
      capabilityProps.getFiles = () => {
        return files.map((f) => {
          const isValidFile = isValidFileBeforeUpload(f.name, f.type);

          return {
            file: {
              type: f.type,
              name: f.name,
              size: f.size,
              file: f,
            },
            isValidFile: isValidFile,
            description: !isValidFile ? unsupportedFileTypeDefaultMsg : '',
          };
        });
      };
      const upload = uploadCapability.handlerOverride;

      await upload(apiOptions, capabilityProps);
    } catch (err) {
      notification.error({
        message: 'Upload failed',
        top: 55,
        duration: 10,
        getContainer: rootContainer,
        description: err.message ? err.message : 'Something went wrong',
      });
    }
  };

  handleResourceItemClick = async ({ event, number, rowData }) => {
    this.props.onResourceItemClick({ event, number, rowData });
  };

  handleResourceItemRightClick = async ({ event, number, rowData }) => {
    this.props.onResourceItemRightClick({ event, number, rowData });
  };

  handleResourceItemDoubleClick = async ({ event, number, rowData }) => {
    const { loadingView } = this.state;
    const { id } = rowData;

    if (loadingView) {
      return;
    }

    const isDirectory = rowData.type === ItemTypes.Directory;
    if (isDirectory) {
      this.navigateToDir(id);
    }

    const isFile = rowData.type === ItemTypes.File;
    if (isFile) {
      const { initializedCapabilities } = this.state;

      const selectForSendCapability = find(initializedCapabilities, (o) => o.id === 'selectForSend');
      if (selectForSendCapability) {
        // weak reference to send files connector
        selectForSendCapability.handler();
      } else {
        // otherwise usual logic
        const previewFileCapability = find(initializedCapabilities, (o) => o.id === 'previewFile');
        if (previewFileCapability) {
          previewFileCapability.handler();
        }
      }
    }

    this.props.onResourceItemDoubleClick({ event, number, rowData });
  };

  handleViewKeyDown = async (e) => {
    const { api, apiOptions } = this.props;
    const { loadingView } = this.state;

    if ((e.which === 13 || e.which === 39) && !loadingView) {
      // Enter key or Right Arrow
      const { selection } = this.state;
      if (selection.length === 1) {
        // Navigate to selected resource if selected resource is single and is directory
        const selectedResourceChildren = this.getResourceChildrenBySelection(selection);

        if (!selectedResourceChildren[0]) {
          // Fix for fast selection updates
          return;
        }

        const isDirectory = selectedResourceChildren[0].type === 'dir';

        if (isDirectory) {
          this.navigateToDir(selectedResourceChildren[0].id);
        }
      }
    }

    if ((e.which === 8 || e.which === 37) && !loadingView) {
      // Backspace or Left Arrow
      // Navigate to parent directory
      const { resource } = this.state;
      const parentId = await api.getParentIdForResource(apiOptions, resource);
      if (parentId) {
        this.navigateToDir(parentId, resource.id);
      }
    }
  };

  handleKeyDown = async (e) => {};

  handleViewRef = (ref) => {
    this.viewRef = ref;
  };

  handleColumnResize = ({ dataKey, newWidth }) => {
    this.setState((prevState) => {
      const updatedWidths = {
        ...prevState.columnWidths,
        [dataKey]: newWidth,
      };

      const totalColumnWidth = Object.values(updatedWidths).reduce((sum, w) => sum + w, 0);

      return {
        columnWidths: updatedWidths,
        totalColumnWidth: totalColumnWidth + 100,
      };
    });
  };

  handleResizeTable = ({ width }) => {
    this.setState((prevState) => {
      const totalColumnWidth = Object.values(prevState.columnWidths).reduce((total, colWidth) => total + colWidth, 0);

      return {
        totalColumnWidth: Math.max(totalColumnWidth, width),
      };
    });
  };

  showDialog = (rawDialogElement) => {
    const dialogElement = rawToReactElement(rawDialogElement);

    this.setStateAsync({ dialogElement });
  };

  hideDialog = () => {
    this.setStateAsync({ dialogElement: null });
  };

  updateNotifications = (notifications) => {
    this.setStateAsync({ notifications });
  };

  getCapabilitiesProps = () => ({
    showDialog: this.showDialog,
    hideDialog: this.hideDialog,
    updateNotifications: this.updateNotifications,
    navigateToDir: this.navigateToDir,
    getSelection: () => this.state.selection,
    getSelectedResources: () => this.state.resourceChildren.filter((o) => this.state.selection.some((s) => s === o.id)),
    getResource: () => this.state.resource,
    getResourceUrl: this.props.api.getResourceUrl,
    getResourceChildren: () => this.state.resourceChildren,
    getResourceLocation: () => this.state.resourceLocation,
    getNotifications: () => this.state.notifications,
    getSortState: () => ({
      sortBy: this.state.sortBy,
      sortDirection: this.state.sortDirection,
    }),
    search: (name) => this.search(name),
    selectFilesToSend: this.props.onSelectFilesToSend,
    rootContainer: this.props.rootContainer,
    refresh: this.handleRefresh,
    controlConfiguration: this.props.controlConfiguration,
    ownerType: this.props.ownerType,
    setParentsForResource: (resourceId) => this.setParentsForResource(resourceId),
  });

  search = async (value) => {
    const { api } = this.props;

    if (!value && this.state.resource) {
      this.navigateToDir(this.state.resource.id, null, true, false);

      return;
    }

    this.setStateAsync({ loadingView: true });

    const searchFiles = await api.search(this.props.apiOptions, {
      parentId: this.state.resource.id,
      searchValue: value,
      orderBy: this.state.sortBy,
      orderDirection: this.state.sortDirection,
    });
    this.handleResourceChildrenChange(searchFiles, value);
    this.setStateAsync({ loadingView: false });
  };

  toggleLocationBar = () => {
    if (this._isMounted) {
      this.setStateAsync({ locationBarVisible: !this.state.locationBarVisible });
    }
  };

  getContextCapabilities = ({ context, isDataView = false }) => {
    const { apiOptions } = this.props;
    const { initializedCapabilities } = this.state;
    return initializedCapabilities
      .filter((capability) => (isDataView ? capability.shouldBeAvailable(apiOptions) : true) && hasContext(capability, context))
      .map((capability) => {
        const res = {
          id: capability.id,
          icon: capability.icon,
          label: capability.label || '',
          onClick: capability.handler || (() => {}),
        };

        if (!isDataView) {
          res.disabled = !capability.shouldBeAvailable(apiOptions);
        }
        return res;
      });
  };

  render() {
    const { id, api, apiOptions, className, listViewLayout, signInRenderer, viewLayoutOptions, disableToolbar, rootContainer } = this.props;

    const {
      apiInitialized,
      apiSignedIn,
      dialogElement,
      history,
      loadingResourceLocation,
      loadingView,
      notifications,
      resourceChildren,
      resourceLocation,
      resource,
      selection,
      sortBy,
      sortDirection,
      searchString,
      locationBarVisible,
      totalColumnWidth,
      columnWidths,
    } = this.state;

    let viewLoadingElement = null;

    if (!apiInitialized) {
      viewLoadingElement = 'Problems with server connection';
    }

    if (!apiSignedIn) {
      viewLoadingElement = signInRenderer ? signInRenderer() : '';
    }

    if (dialogElement) {
      viewLoadingElement = dialogElement;
    }

    const viewLoadingOverlay = viewLoadingElement ? <div className="oc-fm--file-navigator__view-loading-overlay">{viewLoadingElement}</div> : null;

    const rowContextMenuItems = this.getContextCapabilities({
      context: 'row',
      isDataView: true,
    });
    const filesViewContextMenuItems = this.getContextCapabilities({
      context: 'files-view',
      isDataView: true,
    });
    const toolbarItems = this.getContextCapabilities({ context: 'toolbar' });
    const newButtonItems = this.getContextCapabilities({
      context: 'new-button',
    });

    return (
      <div id={id} className={`oc-fm--file-navigator ${className}`} onKeyDown={this.handleKeyDown} ref={(ref) => (this.containerRef = ref)}>
        {viewLoadingOverlay}
        {!disableToolbar ? (
          <div className="oc-fm--file-navigator__toolbar">
            <Toolbar
              items={toolbarItems}
              newButtonItems={newButtonItems}
              history={history}
              adminMode={this.props.controlConfiguration.isAdminUser}
              onMoveForward={this.handleHistoryChange}
              onMoveBackward={this.handleHistoryChange}
              onRefresh={this.handleRefresh}
              onCopyDefaultFolderStructure={this.handleCopyDefaultFolderStructure}
              locale={apiOptions.locale}
              search={this.search}
              permissions={this.props.permissions}
            />
          </div>
        ) : null}

        <Divider style={{ margin: '0' }} />

        <div className="oc-fm--file-navigator__view-container">
          <div className={'oc-fm--file-navigator__location-bar ' + (locationBarVisible ? null : 'hidden')}>
            <LocationBar
              rootContainer={rootContainer}
              controlConfiguration={this.props.controlConfiguration}
              currentResource={resource}
              items={resourceLocation}
              locationChange={(resourceId) => this.handleLocationBarChange(resourceId)}
              loading={loadingResourceLocation}
              onUploadDropedFiles={this.handleUploadDropedFiles}
              onMoveItems={this.handleMoveItems}
              getFolderChildrenForId={async (id) => await this.getFolderChildrenForId(id)}
              permissions={this.props.permissions}
              ownerType={this.props.ownerType}
            />
          </div>

          <div
            onClick={this.toggleLocationBar}
            title={(locationBarVisible ? 'Collapse' : 'Expand') + ' the left pane '}
            style={{ border: '0px' }}
            className="ant-divider ant-divider-vertical collapsible-vertical-divider"
          >
            <Button icon={locationBarVisible ? <LeftOutlined /> : <RightOutlined />} size="small" />
          </div>

          <div className="oc-fm--file-navigator__view">
            {loadingView ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <Spin />
              </div>
            ) : (
              <ListView
                onKeyDown={this.handleViewKeyDown}
                onRowClick={this.handleResourceItemClick}
                onRowRightClick={this.handleResourceItemRightClick}
                onRowDoubleClick={this.handleResourceItemDoubleClick}
                onSelection={this.handleSelectionChange}
                onSort={this.handleSort}
                onMoveItems={this.handleMoveItems}
                onUploadDropedFiles={this.handleUploadDropedFiles}
                onRef={this.handleViewRef}
                loading={loadingView}
                selection={selection}
                sortBy={sortBy}
                sortDirection={sortDirection}
                items={resourceChildren}
                layout={listViewLayout}
                layoutOptions={viewLayoutOptions}
                currentResourceId={this.state.resource.id}
                searchString={searchString}
                currentResourceType={this.state.resource.type}
                currentResourceCapability={this.state.resource.capabilities}
                permissions={this.props.permissions}
                rootContainer={rootContainer}
                rowContextMenuItems={rowContextMenuItems}
                filesViewContextMenuItems={filesViewContextMenuItems}
                ownerType={this.props.ownerType}
                isOnCertsPage={this.props.isOnCertsPage}
                handleResizeTable={this.handleResizeTable}
                handleColumnResize={this.handleColumnResize}
                totalColumnWidth={this.state.totalColumnWidth}
                columnWidths={this.state.columnWidths}
              />
            )}
          </div>
        </div>

        {this.props.children}

        <Notifications notifications={notifications} rootContainer={rootContainer} />
        <div id="create-note-from-file-root-lazy"></div>
      </div>
    );
  }
}

FileNavigator.propTypes = propTypes;
FileNavigator.defaultProps = defaultProps;
